import { gql, useLazyQuery } from '@apollo/client';
import { msg } from '@constants/messages';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { isMonitoringObject, isStatistics } from '@utils/objectType';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { create } from 'react-modal-promise';
import * as yup from 'yup';
import CommonModal from '../../../../components/CommonModal';
import useIsFieldRequired from '../../../../hooks/formik/useIsFieldRequired';
import CustomSelect from '../../../CustomSelect';
import ObjectsAutocomplete from '../../../forms/ObjectsAutocomplete';
import SchemasAutocomplete from '../../../forms/SchemasAutocomplete';

const GET_OBJECT_BY_ID = gql`
  query getObject($id: UUID!) {
    object(id: $id) {
      id
      name
      schemaTags
      schemaId
      schemaType
    }
  }
`;

const LinkWidgetModal = (props) => {
  const objectTypes = [
    { value: 'device', title: 'Device' },
    { value: 'dataset', title: 'Dataset' },
    { value: 'monitor', title: 'Monitor object' },
    { value: 'stat', title: 'Statistic object' },
    { value: 'application', title: 'Application' },
  ];

  const submit = () => props.onResolve();
  const reject = () => props.onReject();

  const handleClose = () => reject();

  const validationSchema = yup.object({
    schemaType: yup.string().trim().required('Schema Type is required'),
    schemaName: yup.string().trim().nullable().required('Schema is required'),
    objectId: yup.string().trim().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      schemaType: 'device',
      schemaName: '',
      objectId: props.objectId || '',
    },
    validationSchema,
    onSubmit: (valuesForm) => {
      props.save({ id: valuesForm.objectId });
      handleClose();
    },
  });

  const [getObjectInfo] = useLazyQuery(GET_OBJECT_BY_ID, {
    variables: {
      id: props.objectId,
    },
  });

  useEffect(() => {
    if (props.objectId) {
      // eslint-disable-next-line @typescript-eslint/require-await
      getObjectInfo().then(async ({ data }) => {
        formik.setFieldValue('objectId', data.object.id);
        formik.setFieldValue('schemaName', data.object.schemaId);

        if (isStatistics(data.object.schemaTags)) {
          formik.setFieldValue('schemaType', 'stat');
        } else if (isMonitoringObject(data.object.schemaTags)) {
          formik.setFieldValue('schemaType', 'monitor');
        } else {
          formik.setFieldValue('schemaType', data.object.schemaType?.toLowerCase());
        }
      });
    }
  }, [props.objectId]);

  const isFieldRequired = useIsFieldRequired(validationSchema);

  return (
    <>
      <CommonModal
        modalOpen={props.isOpen}
        title={'Select object'}
        handleClose={handleClose}
        buttons={
          <>
            <Button onClick={handleClose} color="inherit" data-test-cancel="cancelButton">
              {msg.linkWidgetModal.buttonCancel}
            </Button>
            <Button color="primary" onClick={formik.handleSubmit} data-test-add="addButton" type="submit">
              {msg.linkWidgetModal.buttonSave}
            </Button>
          </>
        }
      >
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <CustomSelect
              name="schemaType"
              label="Schema type"
              value={formik.values.schemaType}
              list={objectTypes}
              onChange={formik.handleChange}
              error={formik.touched.schemaType && Boolean(formik.errors.schemaType)}
              helperText={formik.touched.schemaType && formik.errors.schemaType}
              required={isFieldRequired('schemaType')}
            />
          </Grid>
          <Grid item>
            <SchemasAutocomplete
              type={formik.values.schemaType}
              name="schemaName"
              schemaId={formik.values.schemaName}
              handleChange={(e) => {
                formik.handleChange(e);

                if (!e.target.value) {
                  formik.setFieldValue('objectId', null);
                }
              }}
              error={formik.touched.schemaName && Boolean(formik.errors.schemaName)}
              helperText={formik.touched.schemaName && formik.errors.schemaName}
              required={isFieldRequired('schemaName')}
            />
          </Grid>
          <Grid item>
            <ObjectsAutocomplete
              schemaId={formik.values.schemaName}
              objectId={formik.values.objectId}
              name="objectId"
              handleChange={formik.handleChange}
              error={formik.touched.objectId && Boolean(formik.errors.objectId)}
              helperText={formik.touched.objectId && formik.errors.objectId}
              required={isFieldRequired('objectId')}
            />
          </Grid>
        </Grid>
      </CommonModal>
    </>
  );
};

export default create(LinkWidgetModal);
